import { create } from "zustand";
import * as Sentry from "@sentry/react";
import { BLANK_TIPTAP_JSON, extractTextFromTipTap, generateId } from "../../../../../src/shared/schemas/CharacterSchema";
import produce from "immer";
import { MIN_DOC_TITLING_WORDS, POVS, TENSES } from "../../../../../src/shared/constants";
import { normalizeIsMultiplePOVState } from "../../../../../src/shared/utils/isMultiplePOVState";
import { countWords } from "src/Utils";
import POVTense from "src/models/POVTense";
import Project from "src/models/Project";
import Subscription from "src/models/Subscription";
import { isAutoPOV } from "../../../../../src/shared/constants";
import Auth from "src/Auth";
import { useSeriesStore } from "src/stores/seriesStore";
import { AppEventPublisher } from "../../../AppEventPublisher";
import { useStoryBibleStore } from "src/stores/storyBibleStore";
import { useStickyTourStore } from "../../../stores/stickyTourStore";
import { TourSignal } from "../../StickyTour/TourTypes";

import { normalizeOutlineItemsField } from "../../../../../src/shared/utils/normalizeOutlineItemsField";

let povTenseBatchRequest = 0;
const sameOutlineOwner = (outline, context) => outline?.projectId === context.projectId && outline?.id === context.outlineId && outline?.documentId === context.documentId;

const initialState = {
  outline: { content: { instructions: "", items: [], showGoToChat: false } },
  localDirty: false,
  pendingSettings: {},
  readOnly: false,
  restoring: false,
  rewriteOpen: false,
  isLoading: false,
  chapterDiffs: {}, // Map of chapterId -> { oldContent: { name, summary }, newContent: { name, summary } }
  povTenseMap: {}, // Map of povTenseId -> { pov, tense } — batch-loaded for all chapters
  povTenseMapLoaded: false, // true once the batch fetch has completed
};

const normalizeOutline = (outline) => ({
  ...(outline || {}),
  content: {
    instructions: "",
    showGoToChat: false,
    ...(outline?.content || {}),
    items: normalizeOutlineItemsField(outline?.content?.items) ?? [],
  },
});

function paragraphFromPlainTextBlock(blockText) {
  const lines = blockText;
  const content = [];

  lines.forEach((line, index) => {
    if (line) {
      content.push({ type: "text", text: line });
    }
    if (index < lines.length - 1) {
      content.push({ type: "hardBreak" });
    }
  });

  return {
    type: "paragraph",
    content,
  };
}

function plainTextToTiptap(text = "", options = {}) {
  const { maxConsecutiveInternalEmptyParagraphs = Infinity } = options;
  const rawLines = String(text ?? "").replace(/\r\n?/g, "\n").split("\n");
  let start = 0;
  let end = rawLines.length - 1;
  while (start <= end && rawLines[start].trim().length === 0) start += 1;
  while (end >= start && rawLines[end].trim().length === 0) end -= 1;
  if (start > end) {
    return BLANK_TIPTAP_JSON;
  }

  const paragraphs = [];
  let currentParagraphLines = [];
  let emptyLineRun = 0;

  for (let i = start; i <= end; i += 1) {
    const line = rawLines[i];
    if (line.trim().length === 0) {
      if (currentParagraphLines.length > 0) {
        paragraphs.push(paragraphFromPlainTextBlock(currentParagraphLines));
        currentParagraphLines = [];
      }
      emptyLineRun += 1;
      continue;
    }

    if (emptyLineRun > 1) {
      const extraEmptyParagraphs = emptyLineRun - 1;
      const allowedExtraParagraphs = Math.min(extraEmptyParagraphs, maxConsecutiveInternalEmptyParagraphs);
      for (let p = 0; p < allowedExtraParagraphs; p += 1) {
        paragraphs.push({ type: "paragraph", content: [] });
      }
    }
    emptyLineRun = 0;
    currentParagraphLines.push(line);
  }

  if (currentParagraphLines.length > 0) {
    paragraphs.push(paragraphFromPlainTextBlock(currentParagraphLines));
  }

  return {
    type: "doc",
    content: paragraphs.length ? paragraphs : [{ type: "paragraph", content: [] }],
  };
}

const TRAILING_NUMBER_REGEX = /((?:\d+\s+\d+\/\d+)|(?:\d+\/\d+)|(?:\d+(?:\.\d+)?))(?!.*\d)/;
const FRACTION_WITH_WHOLE_REGEX = /^(\d+)\s+(\d+)\/(\d+)$/;
const SIMPLE_FRACTION_REGEX = /^(\d+)\/(\d+)$/;
const FRACTION_DENOMINATORS = [2, 4, 8, 16, 32, 64];
const EPSILON = 1e-6;

const hasLaterChapterBeforeNextAct = (items, startIndex) => {
  let encounteredNextAct = false;

  for (let i = startIndex + 1; i < items.length; i += 1) {
    const item = items[i];
    if (item.type === "chapter") {
      return true;
    }
    if (item.type === "act") {
      if (encounteredNextAct) {
        return false;
      }
      encounteredNextAct = true;
    }
  }

  return false;
};

const trimTrailingZeroes = (value) => {
  if (!value.includes(".")) {
    return value;
  }

  return value.replace(/(\.\d*?[1-9])0+$|\.0+$/, "$1");
};

const gcd = (a, b) => {
  if (!b) return a;
  return gcd(b, a % b);
};

const formatFractionalNumber = (number) => {
  const whole = Math.floor(number);
  const remainder = number - whole;

  if (Math.abs(remainder) < EPSILON) {
    return `${whole}`;
  }

  for (const denominator of FRACTION_DENOMINATORS) {
    const numerator = Math.round(remainder * denominator);

    if (numerator <= 0 || numerator >= denominator) {
      continue;
    }

    const fractionValue = numerator / denominator;
    if (Math.abs(remainder - fractionValue) < EPSILON) {
      const divisor = gcd(numerator, denominator);
      const simplifiedNumerator = numerator / divisor;
      const simplifiedDenominator = denominator / divisor;
      const fractionPart = `${simplifiedNumerator}/${simplifiedDenominator}`;
      return whole > 0 ? `${whole} ${fractionPart}` : fractionPart;
    }
  }

  return trimTrailingZeroes(number.toString());
};

const extractTrailingNumber = (name) => {
  if (!name) return null;
  const match = name.match(TRAILING_NUMBER_REGEX);
  if (!match) return null;
  const token = match[1].trim();

  const mixedFractionMatch = token.match(FRACTION_WITH_WHOLE_REGEX);
  if (mixedFractionMatch) {
    const whole = Number(mixedFractionMatch[1]);
    const numerator = Number(mixedFractionMatch[2]);
    const denominator = Number(mixedFractionMatch[3]);
    if (!Number.isNaN(whole) && !Number.isNaN(numerator) && !Number.isNaN(denominator) && denominator !== 0) {
      return whole + numerator / denominator;
    }
  }

  const simpleFractionMatch = token.match(SIMPLE_FRACTION_REGEX);
  if (simpleFractionMatch) {
    const numerator = Number(simpleFractionMatch[1]);
    const denominator = Number(simpleFractionMatch[2]);
    if (!Number.isNaN(numerator) && !Number.isNaN(denominator) && denominator !== 0) {
      return numerator / denominator;
    }
  }

  const parsed = Number(token);
  return Number.isNaN(parsed) ? null : parsed;
};

const applyNumberToTemplate = (template, label, number) => {
  const formattedNumber = formatFractionalNumber(number);
  const fallbackLabel = label || "Chapter";

  if (!template) {
    return `${fallbackLabel} ${formattedNumber}`;
  }

  const match = template.match(TRAILING_NUMBER_REGEX);
  if (!match || match.index == null) {
    return `${fallbackLabel} ${formattedNumber}`;
  }

  const prefix = template.slice(0, match.index);
  return `${prefix}${formattedNumber}`.trim();
};

const deriveMiddleItemName = (items, targetIndex, type, label) => {
  const itemsBeforeTarget = items.slice(0, targetIndex + 1);
  const previousItem = [...itemsBeforeTarget].reverse().find((item) => item.type === type);
  const nextItem = items.slice(targetIndex + 1).find((item) => item.type === type);
  const prevNumber = extractTrailingNumber(previousItem?.name);
  const nextNumber = extractTrailingNumber(nextItem?.name);
  let derivedNumber = null;

  if (prevNumber != null && nextNumber != null) {
    const difference = nextNumber - prevNumber;
    derivedNumber = difference > 0 ? prevNumber + difference / 2 : prevNumber + 0.5;
  } else if (prevNumber != null) {
    derivedNumber = prevNumber + 0.5;
  } else if (nextNumber != null) {
    derivedNumber = Math.max(nextNumber - 0.5, 1);
  }

  if (derivedNumber != null) {
    return applyNumberToTemplate(previousItem?.name || nextItem?.name, label, derivedNumber);
  }

  const precedingItems = itemsBeforeTarget.filter((item) => item.type === type).length;
  return `${label} ${formatFractionalNumber(precedingItems + 1)}`;
};

const getNextWholeNumberName = (items, type, label, searchEndIndex) => {
  const fallback = items.filter((item) => item.type === type).length + 1;
  const effectiveEnd = searchEndIndex == null ? items.length : Math.min(searchEndIndex, items.length);
  for (let i = effectiveEnd - 1; i >= 0; i -= 1) {
    const current = items[i];
    if (current.type !== type) continue;
    const lastNumber = extractTrailingNumber(current.name);
    if (lastNumber == null) continue;
    const nextNumber = Math.floor(lastNumber) + 1;
    return `${label} ${nextNumber}`;
  }
  return `${label} ${fallback}`;
};

const updateOutlineSetting = (set, get, key, value) => {
  const { outline, pendingSettings } = get();
  // Before the first load there is no owner to persist this edit against.
  if (!outline.projectId) return;
  set({
    outline: { ...outline, content: { ...outline.content, [key]: value } },
    pendingSettings: {
      ...pendingSettings,
      [outline.projectId]: { ...pendingSettings[outline.projectId], [key]: value },
    },
  });
};

export const useStructuredOutlineStore = create((set, get) => ({
  ...initialState,
  reset: () => set(initialState),
  setLocalClean: () => set({ localDirty: false }),
  discardSettings: (projectId) => {
    const pendingSettings = { ...get().pendingSettings };
    delete pendingSettings[projectId];
    set({ pendingSettings });
  },
  acknowledgeSettings: (saved) => {
    const pending = get().pendingSettings;
    if (!pending[saved.projectId]) return;
    const settings = { ...pending[saved.projectId] };
    Object.entries(saved.settings).forEach(([key, value]) => {
      if (settings[key] === value) delete settings[key];
    });
    const remaining = { ...pending, [saved.projectId]: settings };
    if (!Object.keys(settings).length) delete remaining[saved.projectId];
    set({ pendingSettings: remaining });
  },
  setOutline: (outline, { refreshPOVTense = false } = {}) => {
    const normalized = normalizeOutline(outline);
    const currentOutline = get().outline;
    const ownerChanged = !sameOutlineOwner(normalized, get().getPOVTenseContext());
    const loadPOVTense = !!normalized.projectId && (ownerChanged || refreshPOVTense);
    if (currentOutline?.content?.items && normalized?.content?.items) {
      const collapsedMap = new Map();
      currentOutline.content.items.forEach((item) => {
        if (item.collapsed !== undefined) {
          collapsedMap.set(item.id, item.collapsed);
        }
      });
      if (collapsedMap.size > 0) {
        normalized.content.items = normalized.content.items.map((item) => {
          const localCollapsed = collapsedMap.get(item.id);
          return localCollapsed !== undefined ? { ...item, collapsed: localCollapsed } : item;
        });
      }
    }
    set({
      outline: { ...normalized, content: {
        ...normalized.content,
        ...get().pendingSettings[normalized.projectId],
      } },
      ...(ownerChanged ? { povTenseMap: {}, povTenseMapLoaded: false } : {}),
      ...(loadPOVTense ? { povTenseMapLoaded: false } : {}),
    });
    // Selector and chat navigation both replace outlines here. Readiness must
    // belong to this owner, not to an obsolete batch from the previous book.
    if (loadPOVTense) void get().fetchAllPovTense();
  },
  applyChapterDiffs: (updates = []) => {
    if (!updates || updates.length === 0) return;
    set(
      produce((draft) => {
        const items = draft.outline?.content?.items;
        if (!Array.isArray(items)) return;

        updates.forEach(({ chapterId, name, summary }) => {
          if (!chapterId) return;
          const index = items.findIndex((item) => item.id === chapterId);
          if (index === -1) return;

          const currentItem = items[index];
          const updatedItem = { ...currentItem };

          if (typeof name === "string" && name.length > 0) {
            updatedItem.name = name;
          }

          if (typeof summary === "string") {
            updatedItem.summary = plainTextToTiptap(summary);
          }

          items[index] = updatedItem;
        });
      }),
    );
  },
  setChapterDiff: (chapterId, oldContent, newContent) => {
    set((state) => ({
      chapterDiffs: {
        ...state.chapterDiffs,
        [chapterId]: { oldContent, newContent },
      },
    }));
  },
  clearChapterDiff: (chapterId) => {
    set((state) => {
      const newDiffs = { ...state.chapterDiffs };
      delete newDiffs[chapterId];
      return { chapterDiffs: newDiffs };
    });
  },
  clearAllChapterDiffs: () => {
    set({ chapterDiffs: {} });
  },
  setOutlineItems: (items) => {
    const currentOutline = get().outline;
    set({ outline: { ...currentOutline, content: { ...currentOutline.content, items } } });
    set({ localDirty: true });
  },
  setCustomOutlineInstructions: (instructions) => updateOutlineSetting(set, get, "instructions", instructions),
  setRewriteOpen: (rewriteOpen) => updateOutlineSetting(set, get, "rewriteOpen", rewriteOpen),
  setShowGoToChat: (showGoToChat) => updateOutlineSetting(set, get, "showGoToChat", showGoToChat),
  updateOutlineChapterSummary: (itemId, summary, project) => {
    const { items } = get().outline.content;
    const updatedItems = items.map((item) => {
      if (item.id === itemId) {
        return { ...item, summary };
      }
      return item;
    });

    const itemsAsString = get().getItemsAsString(updatedItems);
    if (project.isUntitled() && countWords(itemsAsString) > MIN_DOC_TITLING_WORDS) {
      project.automaticallyTitleProject(itemsAsString);
    }

    get().setOutlineItems(updatedItems);
    useStickyTourStore.getState().sendFlowSignal(TourSignal.STORY_BIBLE_OUTLINE_EDITED);
  },
  updateItemName: (itemId, name) => {
    const state = get();

    set(
      produce(state, (draft) => {
        for (const item of draft.outline.content.items) {
          if (item.id === itemId) {
            item.name = name;
            draft.localDirty = true;
          }
        }
      })
    );
    useStickyTourStore.getState().sendFlowSignal(TourSignal.STORY_BIBLE_OUTLINE_EDITED);
  },
  deleteItem: (itemId) => {
    const { items } = get().outline.content;
    const updatedItems = items.filter((item) => item.id !== itemId);
    get().setOutlineItems(updatedItems);
    useStickyTourStore.getState().sendFlowSignal(TourSignal.STORY_BIBLE_OUTLINE_EDITED);
  },
  reorderItem: (itemId, targetId) => {
    if (targetId === itemId) {
      // short circuit reorder
      return;
    }
    const { items } = get().outline.content;
    const itemIndex = items.findIndex((item) => item.id === itemId);
    const restOfItems = items.filter((item) => item.id !== itemId);
    if (targetId === "start") {
      if (itemIndex === 0) {
        // short circuit reorder - already at start
        return;
      }
      const updatedItems = [items[itemIndex], ...restOfItems];
      get().setOutlineItems(updatedItems);
      useStickyTourStore.getState().sendFlowSignal(TourSignal.STORY_BIBLE_OUTLINE_EDITED);
      return;
    }
    const sourceTargetIndex = items.findIndex((item) => item.id === targetId);
    if (sourceTargetIndex === itemIndex - 1) {
      // short circuit reorder - dragged to area above current location.
      return;
    }
    // now we find where the target is in the rest of the items.
    const targetIndex = restOfItems.findIndex((item) => item.id === targetId);
    const updatedItems = [...restOfItems];
    updatedItems.splice(targetIndex + 1, 0, items[itemIndex]);
    get().setOutlineItems(updatedItems);
    useStickyTourStore.getState().sendFlowSignal(TourSignal.STORY_BIBLE_OUTLINE_EDITED);
  },
  addChapter: async (afterId, newName, newSummary) => {
    const { items } = get().outline.content;
    const summary = newSummary || BLANK_TIPTAP_JSON;
    const name = newName || "New Chapter";

    const projectId = get().outline.projectId;
    const project = await Project.get(projectId);
    const projectPOVTense = project?.data?.pov_tense;
    const projectMultiplePOV = normalizeIsMultiplePOVState(project?.data?.is_multiple_pov || project?.data?.isMultiplePOV);

    // Use the project POV and tense if it exists (assumes it should be the default), otherwise create a new one.
    // New chapter defaults are untouched even when cloned from project values.
    let povTense = undefined;
    if (projectPOVTense) {
      povTense = await POVTense.create(
        {
          value: projectPOVTense.pov?.value,
          characters: projectPOVTense.pov?.characters || [],
          userChanged: false,
        },
        { value: projectPOVTense.tense?.value ?? projectPOVTense.tense, userChanged: false },
        get().outline.projectId,
        get().outline.userId,
        get().outline.documentId,
      );
    } else if (projectMultiplePOV.value) {
      povTense = await POVTense.create(
        { value: POVS.THIRD_PERSON_LIMITED, characters: [], userChanged: false },
        { value: TENSES.PAST, userChanged: false },
        get().outline.projectId,
        get().outline.userId,
        get().outline.documentId,
      );
    }

    const newChapter = { id: generateId(10), type: "chapter", name, summary, povTenseId: povTense?.id || null };
    let updatedItems;
    switch (afterId) {
      case "beginning":
        AppEventPublisher.storyBible.outline.addChapter("beginning");
        if (!newName) {
          newChapter.name = "Chapter 1";
        }
        updatedItems = [newChapter, ...items];
        break;
      case "end":
        AppEventPublisher.storyBible.outline.addChapter("end");
        if (!newName) {
          newChapter.name = getNextWholeNumberName(items, "chapter", "Chapter");
        }
        updatedItems = [...items, newChapter];
        break;
      default:
        const targetIndex = items.findIndex((item) => item.id === afterId);
        AppEventPublisher.storyBible.outline.addChapter(`middle`);
        if (targetIndex === -1) {
          if (!newName) {
            newChapter.name = getNextWholeNumberName(items, "chapter", "Chapter");
          }
          updatedItems = [...items, newChapter];
        } else {
          if (!newName) {
            const hasLaterChapters = hasLaterChapterBeforeNextAct(items, targetIndex);
            if (hasLaterChapters) {
              newChapter.name = deriveMiddleItemName(items, targetIndex, "chapter", "Chapter");
            } else {
              const precedingItem = items[targetIndex];
              const precedingNumber = precedingItem?.type === "chapter" ? extractTrailingNumber(precedingItem.name) : null;
              newChapter.name = precedingNumber != null ? `Chapter ${Math.floor(precedingNumber) + 1}` : getNextWholeNumberName(items, "chapter", "Chapter", targetIndex + 1);
            }
          }
          updatedItems = [...items.slice(0, targetIndex + 1), newChapter, ...items.slice(targetIndex + 1)];
        }
    }

    get().setOutlineItems(updatedItems);
    useStoryBibleStore.getState().setSectionCollapsed("outline", false);
    useStickyTourStore.getState().sendFlowSignal(TourSignal.STORY_BIBLE_OUTLINE_EDITED);
  },
  addBlankAct: (afterId) => {
    const { items } = get().outline.content;
    const newAct = { id: generateId(10), type: "act", name: "New Act" };
    let updatedItems;
    switch (afterId) {
      case "beginning":
        AppEventPublisher.storyBible.outline.addAct("beginning");
        newAct.name = "Act 1";
        updatedItems = [newAct, ...items];
        break;
      case "end":
        AppEventPublisher.storyBible.outline.addAct("end");
        newAct.name = getNextWholeNumberName(items, "act", "Act");
        updatedItems = [...items, newAct];
        break;
      default:
        AppEventPublisher.storyBible.outline.addAct(`middle`);
        const targetIndex = items.findIndex((item) => item.id === afterId);
        if (targetIndex === -1) {
          newAct.name = getNextWholeNumberName(items, "act", "Act");
          updatedItems = [...items, newAct];
        } else {
          const hasLaterActs = items.slice(targetIndex + 1).some((item) => item.type === "act");
          if (hasLaterActs) {
            newAct.name = deriveMiddleItemName(items, targetIndex, "act", "Act");
          } else {
            const precedingItem = items[targetIndex];
            const precedingNumber = precedingItem?.type === "act" ? extractTrailingNumber(precedingItem.name) : null;
            newAct.name = precedingNumber != null ? `Act ${Math.floor(precedingNumber) + 1}` : getNextWholeNumberName(items, "act", "Act", targetIndex + 1);
          }
          updatedItems = [...items.slice(0, targetIndex + 1), newAct, ...items.slice(targetIndex + 1)];
        }
    }
    get().setOutlineItems(updatedItems);
    useStoryBibleStore.getState().setSectionCollapsed("outline", false);
    useStickyTourStore.getState().sendFlowSignal(TourSignal.STORY_BIBLE_OUTLINE_EDITED);
  },
  deleteOutline: () => {
    const currentOutline = get().outline;
    set({ outline: { ...currentOutline, content: { ...currentOutline.content, items: [] } } });
    set({ localDirty: true });
  },
  setReadOnly: (readOnly) => set({ readOnly }),
  buildChapterOptions: () => {
    return get().outline.content.items.reduce((acc, item) => {
      if (item.type === "chapter") {
        return [...acc, { label: item.name, value: item.id, povTenseId: item.povTenseId }];
      }
      return acc;
    }, []);
  },
  getPromptRepresentation: () => {
    const { items } = get().outline.content;
    if (!items || items.length === 0) {
      return "";
    }

    return items.reduce((acc, item) => {
      const existing = acc.length > 0 ? `${acc}\n\n` : "";
      if (item.type === "act") {
        return `${existing}${item.name}`;
      }
      return `${existing}${item.name}:\n${extractTextFromTipTap(item.summary)}`;
    }, "");
  },
  hasAnyChapterContent: () => {
    const outline = get().outline;
    if (!outline?.content?.items) return false;
    return outline.content.items.some((item) => item.type === "chapter" && extractTextFromTipTap(item.summary));
  },
  setRestoring: (restoring) => {
    set({ restoring });
  },
  buildInsertOptions: () => {
    const { items } = get().outline.content;
    const firstOption = { label: "At the beginning", value: "beginning" };
    const lastOption = { label: "At the end", value: "end" };

    if (!items) return [firstOption, lastOption];
    const itemOptions = items.map((item) => ({
      label: `After ${item.name}`,
      value: item.id,
    }));
    // we replace the last item option with "At the end"
    return [firstOption, ...itemOptions.slice(0, -1), lastOption];
  },
  setGenerationFramework: (generationFramework) => updateOutlineSetting(set, get, "generationFramework", generationFramework),
  setItemCollapse: (itemId, collapsed) => {
    const currentOutline = get().outline;
    const { items } = currentOutline.content;
    const updatedItems = items.map((item) => {
      if (item.id === itemId) {
        return { ...item, collapsed };
      }
      return item;
    });
    set({ outline: { ...currentOutline, content: { ...currentOutline.content, items: updatedItems } } });
  },
  setAllItemsCollapsed: (collapsed) => {
    const { items } = get().outline.content;
    const updatedItems = items.map((item) => ({ ...item, collapsed }));
    get().setOutlineItems(updatedItems);
  },
  getItemsAsString(items) {
    return items.reduce((acc, item) => {
      if (item.type === "act") {
        return acc + item.name + "\n";
      }
      return acc + item.name + "\n" + extractTextFromTipTap(item.summary) + "\n";
    }, "");
  },
  setIsLoading: (isLoading) => {
    set({ isLoading });
  },
  getPOVTenseContext: () => ({ projectId: get().outline?.projectId, outlineId: get().outline?.id, documentId: get().outline?.documentId }),
  fetchAllPovTense: async () => {
    const request = ++povTenseBatchRequest;
    const context = get().getPOVTenseContext();
    let invalidated = false;
    const unsubscribe = useStructuredOutlineStore.subscribe((state) => {
      if (!sameOutlineOwner(state.outline, context)) invalidated = true;
    });
    const cacheAtStart = get().povTenseMap;
    const install = (loaded) => {
      if (invalidated || request !== povTenseBatchRequest) return;
      // A chapter save may have populated this cache after the batch started.
      const newer = Object.fromEntries(Object.entries(get().povTenseMap).filter(([id, value]) => value !== cacheAtStart[id]));
      set({ povTenseMap: { ...loaded, ...newer }, povTenseMapLoaded: true });
    };
    const items = get().outline?.content?.items || [];
    const povTenseIds = items.map((item) => item.povTenseId).filter(Boolean);
    try {
      const povTenseMap = povTenseIds.length ? await Auth.fetchWithIdToken("/api/povTense/batch", { ids: povTenseIds }) : {};
      install(povTenseMap);
    } catch (error) {
      install({});
    } finally {
      unsubscribe();
    }
  },
  updateItemPovTense: async (itemId, povTenseId, { pov, tense }, scenes, context) => {
    // Capture ownership before any authorization or persistence await. Callers
    // also supply their generation guard so retained controls cannot start writes.
    const { projectId, documentId } = context || {};
    const selectedId = useSeriesStore.getState().selectedSeriesProjectId;
    let invalidated = false;
    const assertCurrent = () => {
      const item = get().outline?.content?.items?.find((item) => item.id === itemId);
      if (!projectId || invalidated || !sameOutlineOwner(get().outline, context) || !item || item.povTenseId !== povTenseId || context.isCurrent?.() === false) {
        throw new Error("Chapter POV/tense update interrupted by outline change; any dispatched write may already be saved");
      }
    };
    assertCurrent();
    const unsubscribeOutline = useStructuredOutlineStore.subscribe((state) => {
      if (!sameOutlineOwner(state.outline, context)) invalidated = true;
    });
    const unsubscribeSelection = useSeriesStore.subscribe((state) => {
      if (state.selectedSeriesProjectId !== selectedId) invalidated = true;
    });
    const documents = Object.values(scenes)
      .filter((scene) => scene.itemId === itemId && scene.contents.type === "document")
      .map((scene) => scene.contents.id);
    try {
      const project = await Project.get(projectId);
      if (!project) {
        throw new Error("updateItemPovTense: project not found");
      }

      assertCurrent();
      const userId = project?.data.userId;
      if (!userId) {
        throw new Error("updateItemPovTense: userId not found");
      }

      const subscription = await Subscription.fetchFromUserId(userId);
      if (!subscription) {
        throw new Error("updateItemPovTense: subscription not found");
      }

      const { hasAccessToPOVAndTenseBeta } = subscription.getFeatureFlags();
      if (!hasAccessToPOVAndTenseBeta) {
        throw new Error("updateItemPovTense: no access to POV/Tense beta");
      }

      assertCurrent();
      let characters = [...(pov.characters || [])];
      if (pov.value === POVS.THIRD_PERSON_OMNISCIENT || isAutoPOV(pov.value)) {
        characters = [];
      }

      let povTense;
      if (!povTenseId) {
        // Pass the full tense object so create preserves userChanged (string-only
        // create used to force userChanged: false on first chapter/doc POV rows).
        povTense = await POVTense.create({ ...pov, characters }, tense, projectId, userId, documentId);
      } else {
        povTense = await POVTense.update(userId, povTenseId, { pov: { ...pov, characters }, tense });
      }

      assertCurrent();
      set((currentState) =>
        produce(currentState, (draft) => {
          for (const item of draft.outline.content.items) {
            if (item.id === itemId) {
              item.povTenseId = povTense.id;
              draft.localDirty = true;
            }
          }
          // Update the local cache so chapters don't need to re-fetch
          draft.povTenseMap[povTense.id] = { pov: povTense.pov, tense: povTense.tense };
        })
      );

      // Update the documents with the new povTenseId
      try {
        await Promise.all(documents.map(async (document) => await project.setDocumentPOVTense({ userId, documentId: document, povTenseId: povTense.id })));
      } catch (error) {
        Sentry.captureException(error, { tags: { feature: "structured_outline_chapter_pov_update" } });
      }

      if (invalidated || !sameOutlineOwner(get().outline, context)) throw new Error("Chapter POV/tense completion interrupted by outline change");
      return povTense;
    } finally {
      unsubscribeOutline();
      unsubscribeSelection();
    }
  },
}));

/**
 * Apply outline_update SSE payload synchronously so chapter diffs and summaries
 * land before the stream `complete` event clears CTA loading chrome.
 */
function collectOutlineUpdatesFromChunk(chunk) {
  const chapterIds = Array.isArray(chunk?.chapterIds) ? chunk.chapterIds : [];
  const oldContent = Array.isArray(chunk?.oldContent) ? chunk.oldContent : [];
  const newContent = Array.isArray(chunk?.newContent) ? chunk.newContent : [];
  const outlineUpdates = [];

  chapterIds.forEach((chapterId, index) => {
    const oldC = oldContent[index];
    const newC = newContent[index];
    if (!oldC || !newC) return;

    const hasContentChange = oldC.summary !== newC.summary || oldC.name !== newC.name;
    if (!hasContentChange) return;

    outlineUpdates.push({
      chapterId,
      oldC,
      newC,
      name: newC.name,
      summary: newC.summary,
    });
  });

  return outlineUpdates;
}

let outlineUpdateRefetchGeneration = 0;

/** Mark the start of an async outline refetch; returns a generation token for ordering. */
export function startOutlineUpdateRefetch() {
  outlineUpdateRefetchGeneration += 1;
  return outlineUpdateRefetchGeneration;
}

/** True when this refetch is still the latest outline_update in flight. */
export function isLatestOutlineUpdateRefetch(generation) {
  return generation === outlineUpdateRefetchGeneration;
}

/** @internal Test helper */
export function resetOutlineUpdateRefetchGenerationForTests() {
  outlineUpdateRefetchGeneration = 0;
}

export function applyOutlineUpdateFromChunk(chunk) {
  const store = useStructuredOutlineStore.getState();
  const outlineUpdates = collectOutlineUpdatesFromChunk(chunk);
  if (outlineUpdates.length === 0) return;

  outlineUpdates.forEach(({ chapterId, oldC, newC }) => {
    store.setChapterDiff(chapterId, oldC, newC);
  });
  store.applyChapterDiffs(outlineUpdates);
}

/** Re-apply pending chapter diff summaries after a full outline refetch. */
export function reapplyChapterDiffsToOutline() {
  const { chapterDiffs } = useStructuredOutlineStore.getState();
  const updates = Object.entries(chapterDiffs)
    .map(([chapterId, diff]) => ({
      chapterId,
      name: diff?.newContent?.name,
      summary: diff?.newContent?.summary,
    }))
    .filter((update) => typeof update.summary === "string");

  if (updates.length > 0) {
    useStructuredOutlineStore.getState().applyChapterDiffs(updates);
  }
}

/**
 * Re-apply outline_update summaries from the SSE chunk after refetch, even when
 * chapterDiffs were cleared (e.g. user focused the field before fetch completed).
 */
export function reapplyOutlineUpdateSummariesFromChunk(chunk) {
  const outlineUpdates = collectOutlineUpdatesFromChunk(chunk);
  if (outlineUpdates.length > 0) {
    useStructuredOutlineStore.getState().applyChapterDiffs(outlineUpdates);
  }
}
